// Third-Party
import jwt_decode from "jwt-decode";

// Internal
import { resetLocalStorage } from "src/common/resetLocalStorage";
import { terminateFreshChatWidget } from "src/common/terminateFreshChatWidget";
import { Mixpanel, resetMixpanel } from "src/mixpanel/Mixpanel";
import { getLoginProps } from "src/mixpanel/MixpanelUser";
import { setAccountInfo } from "src/redux/slices/accountInfo";
import { LoginCred, tokenCred } from "src/redux/slices/credentials";
import { loginOrganization } from "src/redux/slices/organization";
import { persistor, store } from "src/redux/store";
import { PATH_DASHBOARD } from "src/routes/paths";

// Types
import { IAccountSession, IUser, Session } from "src/@types/auth";

const ACCOUNTS_KEY = "dost_accounts";
const ADD_ACCOUNT_KEY = "dost_add_account";
const LOGOUT_NOTICE_KEY = "dost_logout_notice";
const SIGNED_IN_NOTICE_KEY = "dost_signed_in_notice";

const ACCOUNT_KEYS = ["company_id", "profile", "customLogo", "discrepancyDetection",
  "askDost_chatHistory", "enableAutoMapping", "connectorId", "notificationAppId",
  "paymentGatewayId", "paymentGatewayOAuth"];

interface IAccountsState {
  availableAccounts: IAccountSession[];
  activeAccountId: string | null;
}

const readState = (): IAccountsState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || "null");
    if (Array.isArray(parsed?.availableAccounts)) return parsed;
  } catch {}
  return { availableAccounts: [], activeAccountId: null };
};

const writeState = (state: IAccountsState) =>
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(state));

export const getAvailableAccounts = () => readState().availableAccounts;

const getAccount = (accountId?: string | null) =>
  getAvailableAccounts().find((account) => account.accountId === accountId);

const toAccountSession = (
  { userId, organizationId, profile, session, version, impersonatedBy }: IUser,
  email: string,
): IAccountSession => ({
  accountId: `${organizationId}:${profile?.userId || userId || email}`,
  accountName: profile?.organizationName || email,
  accessToken: session?.accessToken,
  refreshToken: session?.refreshToken,
  email: profile?.email || email,
  organizationId,
  profile,
  version,
  ...(impersonatedBy && { impersonatedBy }),
});

const saveAccount = (
  accountId: string,
  changes: Partial<IAccountSession>,
  { add = false, activate = false } = {},
) => {
  const state = readState();
  let account = state.availableAccounts.find((a) => a.accountId === accountId);
  if (!account && !add) return;
  if (!account) state.availableAccounts.push((account = { accountId } as IAccountSession));
  Object.assign(account, changes, activate && { lastUsedAt: Date.now() });
  writeState({ ...state, activeAccountId: activate ? accountId : state.activeAccountId });
};

const removeAccount = (accountId: string) => {
  const availableAccounts = getAvailableAccounts().filter((a) => a.accountId !== accountId);
  const [next] = [...availableAccounts].sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0));
  writeState({ availableAccounts, activeAccountId: next?.accountId ?? null });
};

const clearAccountState = () =>
  Object.keys(localStorage)
    .filter((key) => ACCOUNT_KEYS.includes(key) || /^(persist:|redux-)/.test(key))
    .forEach((key) => localStorage.removeItem(key));

let runningAccountId: string | null = null;
let isLeaving = false;

const getUserCred = (): any => store.getState()?.credential?.userCred;

const isSameEmail = (a?: string, b?: string) => !!a && a.toLowerCase() === b?.toLowerCase();

const expiryOf = (token?: string) => {
  try {
    return jwt_decode<{ exp: number }>(token as string).exp;
  } catch {
    return null;
  }
};

export const getRunningAccount = () => getAccount(runningAccountId);

export const getStoredTokens = () => {
  const account = getRunningAccount();
  const exp = expiryOf(account?.accessToken);
  if (!account || !exp || exp * 1000 - 60 * 1000 <= Date.now()) return null;
  return { accessToken: account.accessToken, refreshToken: account.refreshToken };
};

export const getStoredRefreshToken = () => getRunningAccount()?.refreshToken;

export const hasOtherAccounts = () =>
  !!runningAccountId && getAvailableAccounts().some((a) => a.accountId !== runningAccountId);

export const startAddingAccount = () => sessionStorage.setItem(ADD_ACCOUNT_KEY, "1");
export const endAddingAccount = () => sessionStorage.removeItem(ADD_ACCOUNT_KEY);
export const isAddingAccount = () => sessionStorage.getItem(ADD_ACCOUNT_KEY) === "1";

export const takeLogoutNotice = () => {
  const { email, at } = JSON.parse(localStorage.getItem(LOGOUT_NOTICE_KEY) || "{}");
  localStorage.removeItem(LOGOUT_NOTICE_KEY);
  return email && Date.now() - at < 10000 ? (email as string) : null;
};

export const takeSignedInNotice = () => {
  const email = sessionStorage.getItem(SIGNED_IN_NOTICE_KEY);
  sessionStorage.removeItem(SIGNED_IN_NOTICE_KEY);
  return email;
};

export const withAccountLock = <T>(task: () => Promise<T>): Promise<T> => {
  const locks = (navigator as any).locks;
  return locks && runningAccountId
    ? locks.request(`dost-token:${runningAccountId}`, task)
    : task();
};

const initializeAccount = (
  { accessToken, refreshToken, email, organizationId, profile, version }: IAccountSession,
  session: Partial<Session> = { accessToken, refreshToken, firstName: profile?.firstName },
) => {
  const decodedToken = jwt_decode(session.accessToken as string);
  localStorage.setItem("company_id", organizationId);
  localStorage.setItem("profile", JSON.stringify(profile));
  if (!localStorage.getItem("version")) localStorage.setItem("version", version);
  store.dispatch(LoginCred(session, email));
  store.dispatch(tokenCred(decodedToken));
  store.dispatch(loginOrganization(profile));
  store.dispatch(setAccountInfo({ customTheme: profile?.customTheme || "" }));
};

const leaveAccount = () => {
  try {
    resetMixpanel();
  } catch {}
  return Promise.race([
    terminateFreshChatWidget().catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 1000)),
  ]);
};

const identifyAccount = ({ email, organizationId, profile, impersonatedBy }: IAccountSession) => {
  try {
    Mixpanel.register(getLoginProps(profile, organizationId, !!impersonatedBy, impersonatedBy));
    Mixpanel.identify(email);
  } catch {}
};

export const hasUnsavedChanges = () => {
  let unsaved = false;
  const event = new Event("beforeunload", { cancelable: true });
  Object.defineProperty(event, "returnValue", {
    get: () => "",
    set: (value) => {
      if (value) unsaved = true;
    },
  });
  window.dispatchEvent(event);
  return unsaved || event.defaultPrevented;
};

let isSkippingLeavePrompt = false;
const skipLeavePrompt = () => {
  isSkippingLeavePrompt = true;
};
const leavePromptListeners = new WeakMap<EventListenerOrEventListenerObject, EventListener>();
const { addEventListener, removeEventListener } = window;
window.addEventListener = function (this: Window, type, listener, options) {
  if (type === "beforeunload" && listener) {
    if (!leavePromptListeners.has(listener)) {
      leavePromptListeners.set(listener, function (this: Window, event: Event) {
        if (isSkippingLeavePrompt) return undefined;
        return typeof listener === "function"
          ? listener.call(this, event)
          : listener.handleEvent(event);
      });
    }
    return addEventListener.call(this, type, leavePromptListeners.get(listener)!, options);
  }
  return addEventListener.call(this, type, listener, options);
} as typeof window.addEventListener;
window.removeEventListener = function (this: Window, type, listener, options) {
  const registered = (type === "beforeunload" && listener && leavePromptListeners.get(listener)) || listener;
  return removeEventListener.call(this, type, registered, options);
} as typeof window.removeEventListener;

const reload = () => {
  isLeaving = true;
  persistor.pause();
  skipLeavePrompt();
  window.location.replace(PATH_DASHBOARD.root);
};

const changeAccount = async (update: () => void, { force = false } = {}) => {
  if (isLeaving) return;
  isLeaving = true;
  if (!force && hasUnsavedChanges()) {
    await withAccountLock(
      () =>
        new Promise<void>((resolve) => {
          const interactions = ["pointerdown", "keydown"];
          const onPageHide = () => {
            persistor.pause();
            try {
              resetMixpanel();
            } catch {}
            update();
            clearAccountState();
          };
          const onStay = () => {
            window.removeEventListener("pagehide", onPageHide);
            interactions.forEach((type) => window.removeEventListener(type, onStay, true));
            isLeaving = false;
            resolve();
          };
          window.addEventListener("pagehide", onPageHide);
          interactions.forEach((type) => window.addEventListener(type, onStay, true));
          window.location.replace(PATH_DASHBOARD.root);
        }),
    );
    return;
  }
  persistor.pause();
  await new Promise((resolve) => setTimeout(resolve, 50));
  await leaveAccount();
  await withAccountLock(async () => {
    update();
    clearAccountState();
    skipLeavePrompt();
    window.location.replace(PATH_DASHBOARD.root);
    await new Promise(() => {});
  });
};

export const startAccountSession = (onLoaded: () => void) => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith("dost@"))
    .forEach((key) => localStorage.removeItem(key));

  const stored = readState();
  stored.availableAccounts.forEach((account) => {
    if (account.profile?.email) account.email = account.profile.email;
  });
  writeState(stored);

  const userCred = getUserCred();
  const organizationId = localStorage.getItem("company_id");
  const profile = JSON.parse(localStorage.getItem("profile") || "null");
  const hasSession =
    !!userCred?.accessToken && !!organizationId && isSameEmail(userCred.email, profile?.email);
  if (hasSession) {
    const version = localStorage.getItem("version") ?? "";
    const live = toAccountSession({ organizationId, profile, session: userCred, version } as IUser, userCred.email);
    const { accessToken, refreshToken, ...details } = live;
    const known = !!getAccount(live.accountId);
    saveAccount(live.accountId, known ? details : live, { add: true, activate: true });
  }

  const active = getAccount(readState().activeAccountId);
  runningAccountId = active?.accountId ?? null;
  if (active && !hasSession) {
    if (expiryOf(active.accessToken) !== null) {
      resetLocalStorage();
      initializeAccount(active);
      identifyAccount(active);
      onLoaded();
    } else {
      signOutAccount(active.accountId);
    }
  }

  let syncedToken = getUserCred()?.accessToken;
  const stopTokenSync = store.subscribe(() => {
    const { accessToken, refreshToken, email } = getUserCred() || {};
    if (!runningAccountId || !accessToken || accessToken === syncedToken) return;
    if (readState().activeAccountId !== runningAccountId) return;
    if (!isSameEmail(email, getRunningAccount()?.email)) {
      if (!isLeaving) reload();
      return;
    }
    syncedToken = accessToken;
    saveAccount(runningAccountId, { accessToken, refreshToken });
  });
  const onStorage = (event: StorageEvent) => {
    if (isLeaving || (event.key !== ACCOUNTS_KEY && event.key !== null)) return;
    if (readState().activeAccountId !== runningAccountId) reload();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    stopTokenSync();
    window.removeEventListener("storage", onStorage);
  };
};

export const signInAccount = async (data: IUser, email: string) => {
  const account = toAccountSession(data, email);
  if (runningAccountId && getAccount(account.accountId)) {
    sessionStorage.setItem(SIGNED_IN_NOTICE_KEY, account.email);
  }
  saveAccount(account.accountId, account, { add: true });
  endAddingAccount();
  if (runningAccountId && runningAccountId !== account.accountId) {
    if (isLeaving) return false;
    isLeaving = true;
    persistor.pause();
    await new Promise((resolve) => setTimeout(resolve, 50));
    await leaveAccount();
    await withAccountLock(async () => {
      saveAccount(account.accountId, {}, { activate: true });
      clearAccountState();
    });
    runningAccountId = account.accountId;
    initializeAccount(account, data.session);
    skipLeavePrompt();
    setTimeout(() => window.location.replace(PATH_DASHBOARD.root), 500);
    return false;
  }
  saveAccount(account.accountId, {}, { activate: true });
  runningAccountId = account.accountId;
  initializeAccount(account, data.session);
  return true;
};

export const switchAccount = async (accountId: string) => {
  if (accountId === runningAccountId || !getAccount(accountId)) return;
  await changeAccount(() => saveAccount(accountId, {}, { activate: true }));
};

export const reuseSignedInAccount = async (email: string) => {
  if (!runningAccountId || !isAddingAccount()) return false;
  const matches = getAvailableAccounts()
    .filter((account) => account.email?.toLowerCase() === email.toLowerCase())
    .sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0));
  const account = matches.find((a) => a.accountId === runningAccountId) ?? matches[0];
  if (!account) return false;
  sessionStorage.setItem(SIGNED_IN_NOTICE_KEY, account.email);
  endAddingAccount();
  await switchAccount(account.accountId);
  return true;
};

export const signOutAccount = async (accountId = runningAccountId, { notice = false } = {}) => {
  if (!accountId) return;
  if (accountId !== runningAccountId) return removeAccount(accountId);
  await changeAccount(() => {
    const account = getAccount(accountId);
    if (!account) return;
    if (notice) localStorage.setItem(LOGOUT_NOTICE_KEY, JSON.stringify({ email: account.email, at: Date.now() }));
    removeAccount(accountId);
  }, { force: notice });
};

export const endAllSessions = () => {
  if (!runningAccountId || isAddingAccount()) return false;
  persistor.pause();
  skipLeavePrompt();
  [ACCOUNTS_KEY, LOGOUT_NOTICE_KEY].forEach((key) => localStorage.removeItem(key));
  clearAccountState();
  runningAccountId = null;
  return true;
};
